import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RtAutoCarAssignService } from '../rt-auto-car-assign.service';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '@bds/railtrac-internal-services';
import { ErrorListItem } from '@bds/rt-advance-models';
import { EMPTY } from 'rxjs';
import { RtErpcOrigin, RtvAutoCarAssign, RtvAutoCarAssignMetadata } from '@bds/railtrac-models';
import { BdsBusinessGroupService } from '../../bds-business-group/bds-business-group.service';
import { BdsFleetService } from '../../bds-fleet/bds-fleet.service';
import { dxMatStyle } from '@bds/smart-components';
import DataSource from 'devextreme/data/data_source';
import { RtCommodityFact } from '@bds/reference-models';

export interface RtAutoCarAssignDialogData {
    title: string;
}

@Component({
    selector: 'rt-auto-car-assign-details-dialog',
    templateUrl: './rt-auto-car-assign-details-dialog.component.html',
    styleUrls: ['./rt-auto-car-assign-details-dialog.component.css'],
})
export class RtAutoCarAssignDetailsDialogComponent implements OnInit {
    form: UntypedFormGroup;
    iconWarning = faExclamationTriangle;
    isSaving = false;
    errors: ErrorListItem[] = [];

    matStyle: 'fill' | 'outline' | 'standard' | 'legacy' = 'fill';
    dense = true;
    matFloatLabel: 'always' | 'auto' = 'always';
    displayValue = (data: RtErpcOrigin): string => (data ? `${data?.erpcOrigin}` : '');
    displayValueCommClass = (data: RtCommodityFact): string =>
        data ? `${data?.commodityClass} - ${data?.commodityName}` : '';

    businessGroupDxLookupSource!: DataSource;
    fleetDxLookupSource!: DataSource;

    get dxMatStyle(): any {
        return dxMatStyle(this.matStyle);
    }

    SearchDisplay(data: RtErpcOrigin) {
        return data ? `${data?.erpcOrigin}, ${data?.erpcState} (${data?.splc ?? ''})` : '';
    }

    constructor(
        public dialogRef: MatDialogRef<RtAutoCarAssignDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: RtAutoCarAssignDialogData,
        public metadata: RtvAutoCarAssignMetadata,
        private autoCarAssignService: RtAutoCarAssignService,
        private businessGroupApiService: BdsBusinessGroupService,
        private fleetApiService: BdsFleetService,
        private snackbar: MatSnackBar,
    ) {
        this.form = metadata.getFormGroup();
    }

    ngOnInit() {
        this.businessGroupDxLookupSource = new DataSource({
            store: this.businessGroupApiService.getODataStore(),
            paginate: true,
            pageSize: 100,
        });

        this.fleetDxLookupSource = new DataSource({
            store: this.fleetApiService.getODataStore(),
            paginate: true,
            pageSize: 100,
        });
    }

    kvpDisplayTemplate(key: string, value: string): string {
        return key && value ? `${key} - ${value}` : key ?? value ?? '';
    }

    onCommodityChange(event: any): void {
        if (event) {
            this.getControl('commClass').setValue(event.commodityClass);
        }
    }

    onOriginCitySelected(erpc: RtErpcOrigin): void {
        if (erpc) {
            this.getControl('splc').setValue(erpc.splc);
            this.getControl('erpcState').setValue(erpc.erpcState);
            this.getControl('erpcOrigin').setValue(erpc.erpcOrigin);
        }
    }

    private getControl(key: keyof RtvAutoCarAssign): FormControl {
        return this.form.get(key) as FormControl;
    }

    save(): void {
        if (this.form.valid) {
            this.isSaving = true;
            const formValue = this.form.getRawValue();
            formValue.ormId = 0; // Ensure ORM Id is set to 0 for new records

            this.autoCarAssignService
                .createRecord(formValue)
                .pipe(
                    tap({
                        next: (response) => {
                            this.snackbar.open('Record created successfully!', 'Ok', {
                                duration: 3000,
                            });
                            this.dialogRef.close(response);
                        },
                        error: (err) => {
                            this.afterError(err);
                            return EMPTY;
                        },
                    }),
                    catchError((err) => {
                        this.afterError(err);
                        return EMPTY;
                    }),
                    finalize(() => {
                        this.isSaving = false;
                    }),
                )
                .subscribe();
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private afterError(error: any): void {
        if (Array.isArray(error)) {
            this.errors = error as ErrorListItem[];
        } else {
            this.errors = [
                { errorMessage: error?.message || 'An error occurred' } as ErrorListItem,
            ];
        }

        this.snackbar.open('Error creating record.', 'Error', {
            duration: 3000,
            panelClass: 'error-snack',
        });
    }
}
